import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getConfig, graphConfig } from '../authConfig';

interface UserProfile {
  displayName: string;
  userPrincipalName: string;
  photoUrl?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
});

export const useAuth = () => useContext(AuthContext);

// DisabledAuthProvider removed. SSO/OIDC disable logic is no longer needed.

const MsalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const config = getConfig() as ReturnType<typeof getConfig> & {
    ADMIN_GROUP_IDS?: string;
    ADMIN_USERS?: string;
  };

  const normalizeCsv = (raw?: string): string[] =>
    (raw ?? '')
      .split(',')
      .map(v => v.trim().toLowerCase())
      .filter(v => v.length > 0);

  const configuredAdminGroups = normalizeCsv(config.ADMIN_GROUP_IDS);
  const configuredAdminUsers = normalizeCsv(config.ADMIN_USERS);

  const account = accounts.length > 0 ? accounts[0] : null;
  const idTokenClaims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimRoles = Array.isArray(idTokenClaims.roles)
    ? (idTokenClaims.roles as unknown[]).map(role => String(role).toLowerCase())
    : [];
  const claimGroups = Array.isArray(idTokenClaims.groups)
    ? (idTokenClaims.groups as unknown[]).map(group => String(group).toLowerCase())
    : [];
  const userPrincipalName = (account?.username ?? '').toLowerCase();

  const isAdmin =
    claimRoles.includes('admin')
    || configuredAdminGroups.some(groupId => claimGroups.includes(groupId))
    || configuredAdminUsers.includes(userPrincipalName);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (accounts.length > 0) {
        const account = accounts[0];
        
        try {
          // Acquire token silently for background profile loading.
          // Do not trigger interactive popups automatically.
          let accessToken: string = '';
          try {
            const response = await instance.acquireTokenSilent({
              scopes: ['User.Read'],
              account: account,
            });
            accessToken = response.accessToken;
          } catch (error) {
            console.warn('Silent token acquisition failed for profile load. Using account fallback.', error);
            setUser({
              displayName: account.name || 'User',
              userPrincipalName: account.username,
            });
            setIsLoading(false);
            return;
          }

          // Fetch user profile
          const profileResponse = await fetch(graphConfig.graphMeEndpoint, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          
          if (!profileResponse.ok) {
            throw new Error(`Failed to fetch profile: ${profileResponse.status}`);
          }
          
          const profileData = await profileResponse.json();

          // Fetch user photo
          let photoUrl: string | undefined;
          try {
            const photoResponse = await fetch(graphConfig.graphPhotoEndpoint, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            
            if (photoResponse.ok) {
              const photoBlob = await photoResponse.blob();
              photoUrl = URL.createObjectURL(photoBlob);
            } else if (photoResponse.status !== 404) {
              console.warn('Photo fetch failed with status:', photoResponse.status);
            }
          } catch (error) {
            console.warn('Could not fetch profile photo:', error);
          }

          setUser({
            displayName: profileData.displayName || account.name || 'User',
            userPrincipalName: profileData.userPrincipalName || account.username,
            photoUrl,
          });
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Fallback to account info
          setUser({
            displayName: account.name || 'User',
            userPrincipalName: account.username,
          });
        }
      }
      setIsLoading(false);
    };

    fetchUserProfile();
  }, [accounts, instance]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: accounts.length > 0,
        isLoading,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
};
